import { isNullOrUndefined } from '@/utils'

const indentationMultiplier = 16
const indentationBase = 4
const postfix = '!important'
const indentationUnit = 'px'

const convertIndentation = (indentation) => `${indentationBase + (indentation === 0 ? 0 : indentationMultiplier) + (indentation * indentationMultiplier)}${indentationUnit} ${postfix}`

const deconstructBorderStyle = (val) => {
  const valLowerCase = val.toLowerCase()
  let width
  if (valLowerCase.endsWith('thin')) {
    width = 'thin'
  } else if (valLowerCase.endsWith('thick')) {
    width = 'thick'
  } else {
    return { style: valLowerCase }
  }
  const style = valLowerCase.split(width)[0]
  return { style, width }
}

const mapIndentationToPaddingLeft = (indentation) => {
  return isNullOrUndefined(indentation) ? {} : { 'padding-left': convertIndentation(indentation) }
}

const mapHorizontalAlignmentToTextAlign = (alignment) => {
  return isNullOrUndefined(alignment) ? {} : { 'text-align': `${alignment.toLowerCase()} ${postfix}` }
}

const mapColor = (cssProperty, color) => {
  return isNullOrUndefined(color) ? {} : { [cssProperty]: `${color.Color} ${postfix}` }
}

const mapBorderStyle = (cssProperty, style) => {
  if (isNullOrUndefined(style)) {
    return {}
  }
  return { [cssProperty]: `${deconstructBorderStyle(style).style} ${postfix}` }
}

const mapBorderWidth = (cssProperty, style) => {
  if (isNullOrUndefined(style)) {
    return {}
  }
  const width = deconstructBorderStyle(style).width
  if (isNullOrUndefined(width)) {
    return {}
  }
  return { [cssProperty]: `${deconstructBorderStyle(style).width} ${postfix}` }
}

const mapBorder = (borderSide, border) => {
  if (isNullOrUndefined(border)) {
    return {}
  }
  return {
    ...mapColor([`border-${borderSide}-color`], border.Color),
    ...mapBorderStyle([`border-${borderSide}-style`], border.Style),
    ...mapBorderWidth([`border-${borderSide}-width`], border.Style)
  }
}

const mapBackColorToBackgroundColor = (backColor) => {
  return mapColor('background-color', backColor)
}

const mapForeColorToColor = (foreColor) => {
  return mapColor('color', foreColor)
}

const mapFontFamilyToFontFamily = (fontFamily) => {
  return isNullOrUndefined(fontFamily) ? {} : { 'font-family': `${fontFamily} ${postfix}` }
}

const mapItalicToFontStyle = (italic) => {
  return { 'font-style': `${italic ? 'italic' : 'normal'} ${postfix}` }
}

const mapBoldToFontWeight = (bold) => {
  return { 'font-weight': `${bold ? 'bold' : 'normal'} ${postfix}` }
}

const mapUnderlineAndStrikeoutToTextDecoration = (underline, strikeout) => {
  if (!underline && !strikeout) {
    return {}
  }
  let value = underline ? 'underline' : ''
  value += strikeout ? ' line-through' : ''
  value = value.trim() // remove any uneeded white space
  value += ' ' + postfix
  return { 'text-decoration': value }
}

const mapSizeInPointsToFontSize = (sizeInPoints) => {
  return isNullOrUndefined(sizeInPoints) ? {} : { 'font-size': `${sizeInPoints}pt ${postfix}` }
}

const mapTopBorderToBorderTop = (border) => {
  return mapBorder('top', border)
}

const mapBottomBorderToBorderBottom = (border) => {
  return mapBorder('bottom', border)
}

const mapLeftBorderToBorderLeft = (border) => {
  return mapBorder('left', border)
}

const mapRightBorderToBorderRight = (border) => {
  return mapBorder('right', border)
}

const mapTextTransform = (transform) => {
  return isNullOrUndefined(transform) ? {} : { 'text-transform': `${transform.toLowerCase()} ${postfix}` }
}

const mapVerticalAlignmentToJustifyContent = (alignment) => {
  return isNullOrUndefined(alignment) ? {} : { 'justify-content': `${extractVerticalAlignmentStyle(alignment)} ${postfix}` }
}

const extractVerticalAlignmentStyle = (alignment) => {
  if (alignment === 'Top') {
    return 'flex-start'
  } else if (alignment === 'Center') {
    return 'center'
  } else if (alignment === 'Bottom') {
    return 'flex-end'
  } // if "none" or unknown value
  return {}
}

const mapFontToFontProperties = (font) => {
  if (isNullOrUndefined(font)) {
    return {}
  }
  return {
    ...mapFontFamilyToFontFamily(font.FontFamily),
    ...mapItalicToFontStyle(font.Italic),
    ...mapBoldToFontWeight(font.Bold),
    ...mapUnderlineAndStrikeoutToTextDecoration(font.Underline, font.Strikeout),
    ...mapSizeInPointsToFontSize(font.SizeInPoints)
  }
}

const mapStyle = (style) => {
  const cssSelector = '.' + style.UniqueId
  const cssObj = {}
  cssObj[cssSelector] = {
    ...mapIndentationToPaddingLeft(style.Indentation),
    ...mapHorizontalAlignmentToTextAlign(style.HorizontalAlignment),
    ...mapForeColorToColor(style.ForeColor),
    ...mapFontToFontProperties(style.Font),
    ...mapTopBorderToBorderTop(style.TopBorder),
    ...mapBottomBorderToBorderBottom(style.BottomBorder),
    ...mapLeftBorderToBorderLeft(style.LeftBorder),
    ...mapRightBorderToBorderRight(style.RightBorder),
    ...mapBackColorToBackgroundColor(style.BackColor),
    ...mapVerticalAlignmentToJustifyContent(style.VerticalAlignment),
    ...mapTextTransform(style.TextTransform)
  }
  return cssObj
}

const stylesReducer = (mappedStyles, style) => {
  mappedStyles = { ...mappedStyles, ...mapStyle(style) }
  return mappedStyles
}

const mapStylesToCssObjects = (styles) => {
  return styles.reduce(stylesReducer, {})
}

export default {
  mapStylesToCssObjects
}
